import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useReservaFilters } from '../../hooks/useReservaFilters';
import { getEstadoColor, getSubEstadoColor } from '../../constants/reservaColors';
import {
  Box,
  Text,
  Spinner,
  Alert,
  AlertIcon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Button,
} from '@chakra-ui/react';

const MisReservas = () => {
  const { t } = useTranslation();
  const [reservasOriginal, setReservasOriginal] = useState([]);
  const [reservas, setReservas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('fecha_hora_inicio');
  const [sortOrder, setSortOrder] = useState('asc');
  const [mostrarFiltros, setMostrarFiltros] = useState(false);

  // Hook de filtros con vinculación por color
  const {
    estadosActivos,
    subEstadosActivos,
    toggleEstado,
    toggleSubEstado
  } = useReservaFilters(
    {
      'activo': true,
      'inactivo': false,
      'cancelado': false
    },
    {
      'pendiente_pago': false,
      'bolsa': false,
      'asignada_espera': true,
      'en_curso': true,
      'completado_sin_confirmar': false,
      'finalizado': false,
      'pendiente_devolucion': false,
      'fallido': false,
      'cancelado_cliente': false
    }
  );

  const columnsToDisplay = [
    'estado',
    'sub_estado',
    'idioma',
    'tipo',
    'fecha_hora_inicio',
    'fecha_hora_fin',
    'conductor',
    'origen_direccion',
    'destino_direccion',
    'precio',
    'distancia_km',
    'created_at',
  ];

  useEffect(() => {
    const fetchReservas = async () => {
      try {
        const baseURL = process.env.REACT_APP_API_BASE_URL;
        const token = localStorage.getItem('auth-token');
        
        const response = await fetch(`${baseURL}/api/reservas/`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            `Error fetching reservas: ${response.status} - ${
              errorData?.detail || response.statusText
            }`
          );
        }
        const data = await response.json();
        
        // Filtrar solo las reservas del cliente actual
        const storedUser = localStorage.getItem('auth-user');
        if (!storedUser) {
          console.warn('[FILTRO] No hay usuario en localStorage');
          setReservasOriginal([]);
          setReservas([]);
          setLoading(false);
          return;
        }
        
        const userData = JSON.parse(storedUser);
        const userId = userData?.user_id;
        
        if (!userId) {
          console.warn('[FILTRO] No hay user_id en localStorage:', userData);
          setReservasOriginal([]);
          setReservas([]);
          setLoading(false);
          return;
        }

        console.log('[FILTRO] Filtrando reservas para cliente:', userId);
        console.log('[FILTRO] Total reservas recibidas:', data.length);
        
        const reservasCliente = data.filter(reserva => {
          // Filtrar por cliente_id
          const clienteId = String(reserva.cliente_id || '');
          const userIdStr = String(userId);
          const match = clienteId === userIdStr;
          if (match) {
            console.log(`[FILTRO] ✅ Match encontrado: reserva ${reserva.id}`);
          }
          return match;
        });
        
        console.log(`[FILTRO] ✅ Total: ${data.length} reservas, Filtradas: ${reservasCliente.length} para cliente ${userId}`);
        
        setReservasOriginal(reservasCliente);
        setReservas(reservasCliente);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchReservas();
  }, []);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  useEffect(() => {
    // Aplicar filtros primero
    let reservasFiltradas = reservasOriginal.filter(reserva => {
      const estadoActivo = estadosActivos[reserva.estado];
      const subEstadoActivo = subEstadosActivos[reserva.sub_estado];
      return estadoActivo && subEstadoActivo;
    });

    // Luego aplicar ordenamiento
    if (sortBy) {
      const sortedReservas = [...reservasFiltradas].sort((a, b) => {
        const valueA = a[sortBy];
        const valueB = b[sortBy];

        if (typeof valueA === 'string' && typeof valueB === 'string') {
          return sortOrder === 'asc'
            ? valueA.localeCompare(valueB)
            : valueB.localeCompare(valueA);
        } else if (typeof valueA === 'number' && typeof valueB === 'number') {
          return sortOrder === 'asc' ? valueA - valueB : valueB - valueA;
        } else if (typeof valueA === 'boolean' && typeof valueB === 'boolean') {
          return sortOrder === 'asc'
            ? (valueA === valueB ? 0 : (valueA ? 1 : -1))
            : (valueA === valueB ? 0 : (valueA ? -1 : 1));
        } else {
          const stringA = String(valueA);
          const stringB = String(valueB);
          return sortOrder === 'asc'
            ? stringA.localeCompare(stringB)
            : stringB.localeCompare(stringA);
        }
      });
      setReservas(sortedReservas);
    } else {
      setReservas(reservasFiltradas);
    }
  }, [sortBy, sortOrder, reservasOriginal, estadosActivos, subEstadosActivos]);

  const renderCellValue = (reserva, key) => {
    const value = reserva[key];
    
    // Renderizar email del conductor
    if (key === 'conductor') {
      return (
        <Text fontSize="sm">{reserva.conductor_email || 'Sin asignar'}</Text>
      );
    }

    // Renderizar direcciones con fallback a código postal
    if (key === 'origen_direccion') {
      const direccion = reserva.origen_direccion || reserva.origen_codigo_postal || 'N/A';
      return <Text fontSize="sm">{direccion}</Text>;
    }

    if (key === 'destino_direccion') {
      const direccion = reserva.destino_direccion || reserva.destino_codigo_postal || 'N/A';
      return <Text fontSize="sm">{direccion}</Text>;
    }

    // Renderizar idioma con icono de bandera
    if (key === 'idioma') {
      const idiomaLower = value ? value.toLowerCase() : 'es';
      const iconosDisponibles = ['es', 'en', 'de', 'fr', 'it', 'ru', 'ca'];
      const iconoPath = iconosDisponibles.includes(idiomaLower) 
        ? `/assets/icons/${idiomaLower}.png` 
        : '/assets/icons/es.png';
      
      return (
        <Box display="flex" justifyContent="center" alignItems="center">
          <img 
            src={iconoPath} 
            alt={idiomaLower} 
            style={{ width: '24px', height: '24px', objectFit: 'contain' }}
          />
        </Box>
      );
    }
    
    if (value === null || value === undefined) {
      return 'N/A';
    }

    // Renderizar estado con badge de color
    if (key === 'estado') {
      const color = getEstadoColor(value);
      return (
        <Badge colorScheme={color} fontSize="0.9em" px={3} py={1} borderRadius="full">
          {t(`estado_${value}`) || value.toUpperCase()}
        </Badge>
      );
    }

    // Renderizar sub_estado con badge de color
    if (key === 'sub_estado') {
      const color = getSubEstadoColor(value);
      return (
        <Badge colorScheme={color} fontSize="0.9em" px={3} py={1} borderRadius="full">
          {t(`subestado_${value}`) || value.toUpperCase().replace(/_/g, ' ')}
        </Badge>
      );
    }

    // Formatear fechas
    if (key.includes('fecha') || key.includes('created_at') || key.includes('updated_at')) {
      try {
        const date = new Date(value);
        return date.toLocaleString('es-ES', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        });
      } catch {
        return String(value);
      }
    }

    // Formatear números decimales
    if (typeof value === 'number') {
      return value.toFixed(2);
    }

    return String(value);
  };

  const formatColumnHeader = (key) => {
    return t(`col_${key}`) || key
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  if (loading) {
    return (
      <Box p={4} display="flex" justifyContent="center" alignItems="center" minH="200px">
        <Spinner size="xl" />
        <Text ml={4}>{t('conductor_cargando_servicios')}</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={4}>
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={4} borderWidth="1px" borderRadius="lg" overflow="auto">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Text fontSize="xl" fontWeight="bold">
          {t('menu_miovertaxi_mis_reservas')}
        </Text>
        <Button 
          onClick={() => setMostrarFiltros(!mostrarFiltros)}
          colorScheme="blue"
          size="sm"
          leftIcon={<span>🔍</span>}
          className="boton-filtros"
        >
          {t('filtros_boton')}
        </Button>
      </Box>

      {/* Leyenda de colores - Filtros interactivos */}
      {mostrarFiltros && (
      <Box className="filter-section-bg">
        <Text fontSize="sm" fontWeight="semibold" mb={2} color="black">{t('filtros_estados_titulo')}</Text>
        <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
          <Badge 
            colorScheme="blue" 
            fontSize="0.75em" 
            px={2} 
            py={0.5} 
            borderRadius="full"
            cursor="pointer"
            border={estadosActivos['activo'] ? '2px solid white' : '2px solid transparent'}
            opacity={estadosActivos['activo'] ? 1 : 0.4}
            onClick={() => toggleEstado('activo')}
          >
            {t('estado_activo')}
          </Badge>
          <Badge 
            colorScheme="yellow" 
            fontSize="0.75em" 
            px={2} 
            py={0.5} 
            borderRadius="full"
            cursor="pointer"
            border={estadosActivos['inactivo'] ? '2px solid white' : '2px solid transparent'}
            opacity={estadosActivos['inactivo'] ? 1 : 0.4}
            onClick={() => toggleEstado('inactivo')}
          >
            {t('estado_inactivo')}
          </Badge>
          <Badge 
            colorScheme="red" 
            fontSize="0.75em" 
            px={2} 
            py={0.5} 
            borderRadius="full"
            cursor="pointer"
            border={estadosActivos['cancelado'] ? '2px solid white' : '2px solid transparent'}
            opacity={estadosActivos['cancelado'] ? 1 : 0.4}
            onClick={() => toggleEstado('cancelado')}
          >
            {t('estado_cancelado')}
          </Badge>
        </Box>
        
        <Text fontSize="sm" fontWeight="semibold" mb={2} color="white">{t('filtros_subestados_titulo')}</Text>
        <Box display="flex" gap={2} flexWrap="wrap">
          <Badge 
            colorScheme="blue" 
            fontSize="0.75em" 
            px={2} 
            py={0.5} 
            borderRadius="full"
            cursor="pointer"
            border={subEstadosActivos['bolsa'] ? '2px solid white' : '2px solid transparent'}
            opacity={subEstadosActivos['bolsa'] ? 1 : 0.4}
            onClick={() => toggleSubEstado('bolsa')}
          >
            {t('subestado_bolsa')}
          </Badge>
          <Badge 
            colorScheme="blue" 
            fontSize="0.75em" 
            px={2} 
            py={0.5} 
            borderRadius="full"
            cursor="pointer"
            border={subEstadosActivos['asignada_espera'] ? '2px solid white' : '2px solid transparent'}
            opacity={subEstadosActivos['asignada_espera'] ? 1 : 0.4}
            onClick={() => toggleSubEstado('asignada_espera')}
          >
            {t('subestado_asignada_espera')}
          </Badge>
          <Badge 
            colorScheme="blue" 
            fontSize="0.75em" 
            px={2} 
            py={0.5} 
            borderRadius="full"
            cursor="pointer"
            border={subEstadosActivos['en_curso'] ? '2px solid white' : '2px solid transparent'}
            opacity={subEstadosActivos['en_curso'] ? 1 : 0.4}
            onClick={() => toggleSubEstado('en_curso')}
          >
            {t('subestado_en_curso')}
          </Badge>
          <Badge 
            colorScheme="blue" 
            fontSize="0.75em" 
            px={2} 
            py={0.5} 
            borderRadius="full"
            cursor="pointer"
            border={subEstadosActivos['completado_sin_confirmar'] ? '2px solid white' : '2px solid transparent'}
            opacity={subEstadosActivos['completado_sin_confirmar'] ? 1 : 0.4}
            onClick={() => toggleSubEstado('completado_sin_confirmar')}
          >
            {t('subestado_completado_sin_confirmar')}
          </Badge>
          <Badge 
            colorScheme="blue" 
            fontSize="0.75em" 
            px={2} 
            py={0.5} 
            borderRadius="full"
            cursor="pointer"
            border={subEstadosActivos['pendiente_devolucion'] ? '2px solid white' : '2px solid transparent'}
            opacity={subEstadosActivos['pendiente_devolucion'] ? 1 : 0.4}
            onClick={() => toggleSubEstado('pendiente_devolucion')}
          >
            {t('subestado_pendiente_devolucion')}
          </Badge>
          <Badge 
            colorScheme="yellow" 
            fontSize="0.75em" 
            px={2} 
            py={0.5} 
            borderRadius="full"
            cursor="pointer"
            border={subEstadosActivos['pendiente_pago'] ? '2px solid white' : '2px solid transparent'}
            opacity={subEstadosActivos['pendiente_pago'] ? 1 : 0.4}
            onClick={() => toggleSubEstado('pendiente_pago')}
          >
            {t('subestado_pendiente_pago')}
          </Badge>
          <Badge 
            colorScheme="red" 
            fontSize="0.75em" 
            px={2} 
            py={0.5} 
            borderRadius="full"
            cursor="pointer"
            border={subEstadosActivos['cancelado_cliente'] ? '2px solid white' : '2px solid transparent'}
            opacity={subEstadosActivos['cancelado_cliente'] ? 1 : 0.4}
            onClick={() => toggleSubEstado('cancelado_cliente')}
          >
            {t('subestado_cancelado_cliente')}
          </Badge>
        </Box>
      </Box>
      )}

      {reservas.length > 0 ? (
        <Table variant="simple" width="100%" minWidth="1200px">
          <Thead>
            <Tr>
              {columnsToDisplay.map((key) => (
                <Th
                  key={key}
                  cursor="pointer"
                  onClick={() => handleSort(key)}
                >
                  {formatColumnHeader(key)}
                  {sortBy === key && (sortOrder === 'asc' ? ' ▲' : ' ▼')}
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {reservas.map((reserva) => (
              <Tr key={reserva.id}>
                {columnsToDisplay.map((key) => (
                  <Td key={key}>{renderCellValue(reserva, key)}</Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      ) : (
        <Text>No hay reservas para mostrar.</Text>
      )}
    </Box>
  );
};

export default MisReservas;
